import type { PatientDetail } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ActivityExportTab } from './ActivityExportTab';
import { AiChatTab } from './AiChatTab';
import { AppointmentsTab } from './AppointmentsTab';
import { CareTeamTab } from './CareTeamTab';
import { SignUpTab } from './SignUpTab';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { DetailPageSkeleton, ErrorBanner } from '@/components/ui/state-views';
import { getPatient } from '@/config/api-helpers';
import { recordVisit } from '@/hooks/useVisitHistory';
import { formatDate } from '@/utils/format';

function statusLabel(p: PatientDetail) {
  if (p.archivedAt) return 'Archived';
  return p.isActive ? 'Active' : 'Inactive';
}

const FIELDS: { label: string; render: (p: PatientDetail) => string }[] = [
  { label: 'Patient ID', render: (p) => String(p.patientId) },
  { label: 'User ID', render: (p) => String(p.userId) },
  { label: 'Status', render: (p) => statusLabel(p) },
  { label: 'Phase of Care', render: (p) => p.phaseOfCare ?? '\u2014' },
  { label: 'Time Zone', render: (p) => p.timeZone ?? '\u2014' },
  { label: 'Healthie ID', render: (p) => p.healthieId ?? '\u2014' },
  { label: 'Referral Source', render: (p) => p.referralSource ?? '\u2014' },
  { label: 'Referral Provider', render: (p) => p.referralProvider ?? '\u2014' },
  { label: 'Created', render: (p) => formatDate(p.createdAt) },
];

const TABS = ['Care Team', 'Appointments', 'Sign Up', 'AI Chat', 'Activity Export'] as const;
type Tab = (typeof TABS)[number];

function TabContent({ tab, patientId }: { tab: Tab; patientId: number }) {
  switch (tab) {
    case 'Care Team':
      return <CareTeamTab patientId={patientId} />;
    case 'Appointments':
      return <AppointmentsTab patientId={patientId} />;
    case 'Sign Up':
      return <SignUpTab patientId={patientId} />;
    case 'AI Chat':
      return <AiChatTab patientId={patientId} />;
    case 'Activity Export':
      return <ActivityExportTab patientId={patientId} />;
  }
}

export function PatientPage() {
  const { patientId } = useParams();
  const {
    data: patient,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => getPatient(patientId!),
    enabled: patientId != null,
  });
  const [activeTab, setActiveTab] = useState<Tab>('Care Team');

  const displayName = patient?.displayName ?? 'Patient';

  useEffect(() => {
    if (patient) {
      recordVisit(`/patients/${patient.patientId}`, String(patient.patientId), 'patient');
    }
  }, [patient, displayName]);

  return (
    <div className='space-y-6'>
      <Breadcrumbs items={[{ label: 'Patients', to: '/patients' }, { label: displayName }]} />

      {error ? (
        <ErrorBanner
          message={error?.message ?? 'Failed to load patient'}
          onRetry={() => refetch()}
          title='Patient is unavailable'
        />
      ) : isLoading ? (
        <DetailPageSkeleton fields={FIELDS.length} label='Loading patient' tabs={TABS.length} />
      ) : patient ? (
        <div className='space-y-6'>
          <div className='rounded-lg border border-neutral-200 bg-white p-6'>
            <div className='min-w-0'>
              <h2 className='font-mediumbold mb-4 text-2xl text-neutral-900'>{displayName}</h2>
              <div className='grid grid-cols-1 gap-x-6 gap-y-2 text-sm text-neutral-700 md:grid-cols-3'>
                {FIELDS.map((f) => (
                  <div key={f.label}>
                    <span className='text-neutral-500'>{f.label}:</span> {f.render(patient)}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className='overflow-hidden rounded-lg border border-neutral-200 bg-white'>
            <div className='flex border-b border-neutral-200'>
              {TABS.map((tab) => (
                <button
                  key={tab}
                  className={`px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'border-b-2 border-neutral-900 text-neutral-900'
                      : 'text-neutral-500 hover:text-neutral-700'
                  }`}
                  onClick={() => setActiveTab(tab)}
                  type='button'
                >
                  {tab}
                </button>
              ))}
            </div>
            <TabContent tab={activeTab} patientId={patient.patientId} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
